import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarComponent } from '../../components/snackbar/snackbar.component';
import { FeeService } from '../fee/fee.service';
import { SpinnerService } from '../spinner/spinner.service';
import { StripeService } from '../stripe/stripe.service';
import { CreateOneTimeRewardPayload, TeamsService } from '../teams/teams.service';
import { UiAlertService } from '../ui-alert/ui-alert.service';

export type OneTimeRewardRecipient =
  | string
  | {
      disabled?: boolean;
      email?: string;
      id?: string;
      inviteSentAt?: string | null;
      memberId?: string;
      message?: string;
      metadata?: Record<string, any> | null;
      removalStatus?: string | null;
      status?: string | null;
      teamUserId?: string;
      userId?: string;
      userEmail?: string;
    };

export interface OneTimeRewardsRequest {
  amount: number;
  message: string;
  note: string;
  destination: boolean;
  recipients: OneTimeRewardRecipient[];
  isP2p: boolean;
  team: any;
  toTeamId: string;
  isUsingWallitBalance: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OneTimeRewardsService {
  constructor(
    private feeService: FeeService,
    private spinnerService: SpinnerService,
    private stripeService: StripeService,
    private teamsService: TeamsService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
  ) {}

  async processOneTimeReward(
    request: OneTimeRewardsRequest,
    onProgress?: (count: number) => void,
  ): Promise<boolean> {
    const recipientEmails = this.getRecipientEmails(request);
    const totalAmount = request.amount * request.recipients.length;

    if (this.shouldUseDirectStripeAllowanceEndpoint(request) && !this.hasDirectRecipients(request)) {
      this.showError(
        'Unable to send one time allowance because some selected members are missing team membership details. Please refresh and try again.',
      );
      return false;
    }

    if (
      this.shouldUseDirectStripeAllowanceEndpoint(request) &&
      this.hasNonSettleableDirectRecipient(request)
    ) {
      this.showError('Some selected members are not eligible for one time allowance rewards.');
      return false;
    }

    if (this.requiresSignedUpRecipients(request) && this.hasPendingObjectRecipient(request)) {
      this.showError('Pending-signup recipients can only receive allowance one time rewards.');
      return false;
    }

    // Validate balance if using Wallit balance
    if (request.isUsingWallitBalance) {
      const balanceCheck = await this.validateWallitBalance(request.team.id, totalAmount);
      if (!balanceCheck.success) {
        this.showError(balanceCheck.message);
        return false;
      }
    }

    // Get confirmation
    const confirmMessage = await this.getConfirmationMessage(request, totalAmount);
    const confirm = await this.uiAlertService.presentAlertConfirm(confirmMessage);

    if (!confirm) return false;

    this.spinnerService.show();
    onProgress?.(0);

    try {
      const selectedPaymentMethod = request.isUsingWallitBalance
        ? 'wallit'
        : await this.teamsService.getPaymentMethod(request.team);

      const result = this.shouldUseDirectStripeAllowanceEndpoint(request)
        ? await this.createDirectStripeAllowanceReward(request, selectedPaymentMethod)
        : await this.createLegacyOneTimeReward(request, recipientEmails, selectedPaymentMethod);
      console.log(`Result`, result);

      onProgress?.(request.recipients.length);
      this.spinnerService.hide();

      let message = `One time payment made. If the used payment method is a bank, this may take a few days.`;
      if (result && result.failedMembers && result.failedMembers.length > 0) {
        message = `One time payment failed for: ${result.failedMembers.join(', ')}.`;
      }
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: message,
      });

      return true;
    } catch (error: any) {
      this.spinnerService.hide();
      this.showError(this.getErrorMessage(error));
      return false;
    }
  }

  private async createDirectStripeAllowanceReward(
    request: OneTimeRewardsRequest,
    selectedPaymentMethod: any,
  ): Promise<any> {
    const paymentMethodId = this.getPaymentMethodId(selectedPaymentMethod);
    const payload: CreateOneTimeRewardPayload = {
      amount: request.amount,
      destination: 'allowance',
      message: request.message,
      note: request.note || null,
      paymentMethodId,
      recipients: this.getDirectRecipients(request),
    };

    return this.teamsService.createOneTimeReward(request.team.id, payload);
  }

  private async createLegacyOneTimeReward(
    request: OneTimeRewardsRequest,
    recipientEmails: string[],
    selectedPaymentMethod: any,
  ): Promise<any> {
    const customerId = await this.teamsService.getCustomerId(request.team);
    const type = request.isP2p ? 'P2P' : 'ONETIME-REWARD';
    const paymentMethodId = this.getPaymentMethodId(selectedPaymentMethod);

    return this.stripeService.makeOneTimePayment(
      customerId,
      `${request.team.name}: One time payment to ${
        recipientEmails.length > 1 ? '' : recipientEmails[0]
      }`,
      request.amount,
      recipientEmails,
      request.message,
      request.note,
      paymentMethodId,
      request.team.id,
      type,
      request.toTeamId,
      request.destination,
    );
  }

  private shouldUseDirectStripeAllowanceEndpoint(request: OneTimeRewardsRequest): boolean {
    return !request.isP2p && !request.isUsingWallitBalance && request.destination === false;
  }

  private requiresSignedUpRecipients(request: OneTimeRewardsRequest): boolean {
    return request.isP2p || request.isUsingWallitBalance || request.destination === true;
  }

  private hasPendingObjectRecipient(request: OneTimeRewardsRequest): boolean {
    return request.recipients.some(
      (recipient) => typeof recipient !== 'string' && !this.getRecipientUserId(recipient),
    );
  }

  private hasNonSettleableDirectRecipient(request: OneTimeRewardsRequest): boolean {
    return request.recipients.some(
      (recipient) =>
        typeof recipient !== 'string' && !this.canSettleDirectStripeAllowance(recipient),
    );
  }

  private canSettleDirectStripeAllowance(recipient: Exclude<OneTimeRewardRecipient, string>): boolean {
    return !this.isRemovedOrDisabled(recipient);
  }

  private isRemovedOrDisabled(recipient: Exclude<OneTimeRewardRecipient, string>): boolean {
    return !!recipient.disabled || this.getRemovalStatus(recipient) === 'deactivationPendingSettlement';
  }

  private getRemovalStatus(recipient: Exclude<OneTimeRewardRecipient, string>): string {
    return (
      recipient.removalStatus ||
      recipient.metadata?.removalStatus ||
      ''
    );
  }

  private hasDirectRecipients(request: OneTimeRewardsRequest): boolean {
    return (
      request.recipients.length > 0 &&
      this.getDirectRecipients(request).length === request.recipients.length
    );
  }

  private getDirectRecipients(request: OneTimeRewardsRequest): CreateOneTimeRewardPayload['recipients'] {
    return request.recipients
      .map((recipient) => this.getRecipientTeamUserId(recipient))
      .filter((teamUserId) => !!teamUserId)
      .map((teamUserId) => ({ teamUserId }));
  }

  private getRecipientEmails(request: OneTimeRewardsRequest): string[] {
    return request.recipients
      .map((recipient) =>
        typeof recipient === 'string'
          ? recipient
          : recipient.email || recipient.userEmail || '',
      )
      .filter((email) => !!email);
  }

  private getRecipientTeamUserId(recipient: OneTimeRewardRecipient): string {
    if (typeof recipient === 'string') {
      return '';
    }
    return recipient.teamUserId || recipient.memberId || recipient.id || '';
  }

  private getRecipientUserId(recipient: OneTimeRewardRecipient): string {
    if (typeof recipient === 'string') {
      return '';
    }
    return recipient.userId || '';
  }

  private getPaymentMethodId(paymentMethod: any): string {
    return typeof paymentMethod === 'string'
      ? paymentMethod
      : paymentMethod?.stripePaymentMethod || paymentMethod?.id || '';
  }

  private getErrorMessage(error: any): string {
    const info = error?.error || error?.data || error;
    const invalidRecipients = info?.details?.invalidRecipients;

    if (Array.isArray(invalidRecipients) && invalidRecipients.length > 0) {
      return `${info?.message || 'Some recipients are invalid'}: ${invalidRecipients
        .map((recipient: any) => recipient.reason || recipient.teamUserId)
        .filter((reason: string) => !!reason)
        .join(', ')}`;
    }

    if (info?.raw?.message) {
      const declineCode = info.raw.decline_code?.split('_').join(' ') || '';
      return `${info.raw.message}${declineCode ? `: ${declineCode}` : ''}`;
    }

    return info?.message || info?.error || error?.message || 'Unable to send one time payment.';
  }

  private async validateWallitBalance(
    teamId: string,
    amount: number,
  ): Promise<{ success: boolean; message: string }> {
    const ownerWallitBalance = await this.teamsService.getTeamOwnerWallitBalance(teamId);

    if (!ownerWallitBalance.success) {
      return {
        success: false,
        message:
          'Unable to verify account balance. Please try again or contact support if the problem persists.',
      };
    }

    if (ownerWallitBalance.data.balance < amount) {
      return {
        success: false,
        message: 'Insufficient funds to send rewards.',
      };
    }

    return { success: true, message: '' };
  }

  private async getConfirmationMessage(
    request: OneTimeRewardsRequest,
    totalAmount: number,
  ): Promise<string> {
    if (request.isUsingWallitBalance) {
      return 'This will make a one time charge to the team owners Wallit Balance.';
    } else {
      const serviceFee = await this.feeService.getPaymentMethodFee(request.team);
      const amountPlusFee = totalAmount * (1 + serviceFee);
      const paymentMethodDescription = 'default payment method';

      return `This will initiate a one time ${
        request.team.invoiceEmail ? 'invoice' : 'charge'
      } of $${amountPlusFee.toFixed(2)} to your ${paymentMethodDescription}. ${
        serviceFee > 0
          ? `This includes a service fee of $${(amountPlusFee - totalAmount).toFixed(2)}.`
          : ''
      } Any available credit balance will be applied first before charging the remaining amount.`;
    }
  }

  private showError(message: string): void {
    this.snackBar.openFromComponent(SnackBarComponent, {
      data: message,
    });
  }
}
